/**
 * Hash routes keep the browser back button, refresh and deep links working
 * without a router dependency, and behave identically inside the Tauri webview.
 */
export type Page = 'devices' | 'people' | 'settings' | 'local';
export type DeviceTab = 'overview' | 'files' | 'logs' | 'resources' | 'nyx';
export type SettingsTab =
  | 'account'
  | 'directory'
  | 'terminal'
  | 'connections'
  | 'updates'
  | 'about';

export interface Route {
  readonly page: Page;
  readonly deviceId?: string;
  readonly tab?: DeviceTab;
  readonly settingsTab?: SettingsTab;
}

const deviceIdPattern = /^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/;
const tabs: readonly DeviceTab[] = ['overview', 'files', 'logs', 'resources', 'nyx'];
export const settingsTabs: readonly SettingsTab[] = [
  'account',
  'directory',
  'terminal',
  'connections',
  'updates',
  'about',
];

export function parseRoute(hash: string, allowLocal: boolean): Route {
  const path = hash.replace(/^#\/?/, '').replace(/\/+$/, '');
  const [head = '', second, third] = path.split('/', 3);
  switch (head) {
    case 'people':
      return { page: 'people' };
    case 'settings': {
      const tab = settingsTabs.find((candidate) => candidate === second && candidate !== 'account');
      return tab ? { page: 'settings', settingsTab: tab } : { page: 'settings' };
    }
    case 'this-device':
      return allowLocal ? { page: 'local' } : { page: 'devices' };
    case 'devices': {
      if (!second) return { page: 'devices' };
      let id: string;
      try {
        id = decodeURIComponent(second);
      } catch {
        return { page: 'devices' };
      }
      if (!deviceIdPattern.test(id)) return { page: 'devices' };
      const tab = tabs.find((candidate) => candidate === third && candidate !== 'overview');
      return tab ? { page: 'devices', deviceId: id, tab } : { page: 'devices', deviceId: id };
    }
    default:
      return { page: 'devices' };
  }
}

export function routeHash(route: Route): string {
  switch (route.page) {
    case 'people':
      return '#/people';
    case 'settings':
      return route.settingsTab && route.settingsTab !== 'account'
        ? `#/settings/${route.settingsTab}`
        : '#/settings';
    case 'local':
      return '#/this-device';
    default: {
      if (!route.deviceId) return '#/devices';
      const base = `#/devices/${encodeURIComponent(route.deviceId)}`;
      return route.tab && route.tab !== 'overview' ? `${base}/${route.tab}` : base;
    }
  }
}
